import { useCallback, useEffect, useState } from 'react';

import { useBaseOwnerId } from '@rhino-project/core/hooks';
import { useModelIndex } from '@rhino-project/core/hooks';
import TeamContext from './TeamContext';
import { getLastTeamIdForOrg, setLastTeamIdForOrg } from '../utils/prefStorage';

type Team = {
  id: string;
  name: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
};

type TeamState = {
  team: Team | null;
};

const CustomContexts = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line @eslint-react/use-state -- Preserve existing behavior while enabling the rule globally.
  const [teamContext, teamContextSet] = useState<TeamState>({ team: null });
  const baseOwnerId = useBaseOwnerId();

  const { resources: teams } = useModelIndex('team', {
    filter: { organization_id: baseOwnerId },
    order: ['created_at'],
    queryOptions: { enabled: !!baseOwnerId }
  });

  useEffect(() => {
    const results = teams?.results as Team[] | undefined;
    if (!results) return;

    const stillValid =
      teamContext.team &&
      results.some((t) => String(t?.id) === String(teamContext.team!.id));
    if (stillValid) return;

    const storedTeamId = baseOwnerId
      ? getLastTeamIdForOrg(String(baseOwnerId))
      : null;
    const storedTeam = storedTeamId
      ? results.find((t) => String(t.id) === storedTeamId)
      : undefined;

    const next = storedTeam ?? results[0] ?? null;
    // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- Preserve existing behavior while enabling the rule globally.
    teamContextSet({ team: next });
    if (next && baseOwnerId) {
      setLastTeamIdForOrg(String(baseOwnerId), String(next.id));
    }
  }, [baseOwnerId, teams, teamContext.team]);

  const setTeamContext = useCallback(
    (team: Team | null) => {
      teamContextSet({ team });
      if (team && baseOwnerId) {
        setLastTeamIdForOrg(String(baseOwnerId), String(team.id));
      }
    },
    [baseOwnerId]
  );

  return (
    <TeamContext value={{ ...teamContext, setContext: setTeamContext }}>
      {children}
    </TeamContext>
  );
};

export default CustomContexts;
